<template>
  <div class="animal-result-card">
    <!-- 主要动物卡片 -->
    <div class="main-card" :style="{ borderColor: animal.color }">
      <!-- 标题区域 -->
      <div class="result-title-section">
        <h1 class="result-title">你的动物性格是</h1>
      </div>

      <div class="card-header" :style="{ backgroundColor: animal.color + '15' }">
        <div class="animal-emoji">{{ animal.emoji }}</div>
        <div class="animal-title">
          <h2 class="animal-name">{{ animal.name }}</h2>
          <div class="match-score">
            匹配度: <span class="score-value">{{ (similarity * 100).toFixed(1) }}%</span>
          </div>
        </div>
      </div>

      <!-- 动物描述 -->
      <div class="card-content">
        <p class="description">{{ animal.description }}</p>

        <!-- 特质标签 -->
        <div class="tags-section">
          <h4 class="section-title">性格标签</h4>
          <div class="tags">
            <span 
              v-for="tag in animal.tags" 
              :key="tag"
              class="tag"
              :style="{ backgroundColor: animal.color + '20', color: animal.color }"
            >
              {{ tag }}
            </span>
          </div>
        </div>

        <!-- 核心特质 -->
        <div class="traits-section">
          <h4 class="section-title">核心特质</h4>
          <ul class="traits-list">
            <li v-for="(trait, index) in animal.traits" :key="index" class="trait-item">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="check-icon" :style="{ color: animal.color }">
                <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                <path d="m9 11 3 3L22 4"></path>
              </svg>
              {{ trait }}
            </li>
          </ul>
        </div>

        <!-- 适合职业 -->
        <div class="careers-section">
          <h4 class="section-title">适合职业</h4>
          <div class="careers">
            <span 
              v-for="career in animal.careers" 
              :key="career"
              class="career-tag"
            >
              {{ career }}
            </span>
          </div>
        </div>

        <!-- 相性配对 -->
        <div class="compatibility-section">
          <h4 class="section-title">相性配对</h4>
          <div class="compatibility-grid">
            <div class="compatibility-item best">
              <div class="compatibility-label">最佳搭档</div>
              <div class="animals">
                <div v-for="name in animal.compatibility.best" :key="name" class="animal-item-with-reason">
                  <div class="animal-header">
                    <span class="animal-emoji-small">{{ getAnimalEmoji(name) }}</span>
                    <span class="animal-name-small">{{ name }}</span>
                  </div>
                  <div class="animal-reason">{{ getCompatibilityReason(getPureAnimalName(animal.name), name) }}</div>
                </div>
              </div>
            </div>
            <div class="compatibility-item good">
              <div class="compatibility-label">和谐相处</div>
              <div class="animals">
                <div v-for="name in animal.compatibility.good" :key="name" class="animal-item-with-reason">
                  <div class="animal-header">
                    <span class="animal-emoji-small">{{ getAnimalEmoji(name) }}</span>
                    <span class="animal-name-small">{{ name }}</span>
                  </div>
                  <div class="animal-reason">{{ getCompatibilityReason(getPureAnimalName(animal.name), name) }}</div>
                </div>
              </div>
            </div>
            <div class="compatibility-item challenging">
              <div class="compatibility-label">需要磨合</div>
              <div class="animals">
                <div v-for="name in animal.compatibility.challenging" :key="name" class="animal-item-with-reason">
                  <div class="animal-header">
                    <span class="animal-emoji-small">{{ getAnimalEmoji(name) }}</span>
                    <span class="animal-name-small">{{ name }}</span>
                  </div>
                  <div class="animal-reason">{{ getCompatibilityReason(getPureAnimalName(animal.name), name) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { animalArchetypes } from '@/data/animalArchetypes'
import { getCompatibilityReason } from '@/utils/compatibilityReasons'

/* eslint-disable no-undef */
defineProps({
  animal: {
    type: Object,
    required: true
  },
  similarity: {
    type: Number,
    required: true
  }
})

// 根据动物名称获取emoji
function getAnimalEmoji(animalName) {
  return animalArchetypes[animalName]?.emoji || '🐾'
}

// 获取纯动物名称（去除后缀）
function getPureAnimalName(fullName) {
  if (typeof fullName === 'string') {
    // 如果包含 " - "，取第一部分
    const parts = fullName.split(' - ')
    return parts[0]
  }
  return fullName
}
/* eslint-enable no-undef */
</script>

<style scoped>
.animal-result-card {
  width: 100%;
}

.result-title {
  font-size: 2.5rem;
  font-weight: bold;
  color: #1a202c;
  margin: 0 0 0.5rem 0;
}

.result-subtitle {
  font-size: 1.1rem;
  color: #718096;
  margin: 0;
}

.main-card {
  background: white;
  border-radius: 16px;
  border: 3px solid;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.result-title-section {
  text-align: center;
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid #e2e8f0;
}

.main-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.card-header {
  padding: 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.animal-emoji {
  font-size: 5rem;
  line-height: 1;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
}

.animal-title {
  flex: 1;
}

.animal-name {
  font-size: 2rem;
  font-weight: bold;
  color: #1a202c;
  margin: 0 0 0.5rem 0;
}

.match-score {
  font-size: 1rem;
  color: #4a5568;
}

.score-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #8b5cf6;
}

.card-content {
  padding: 2rem;
}

.description {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #2d3748;
  margin-bottom: 2rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
  margin: 1.5rem 0 1rem 0;
}

.tags-section {
  margin-bottom: 2rem;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.tag {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
}

.traits-section {
  margin-bottom: 2rem;
}

.traits-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.trait-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
  font-size: 1rem;
  color: #2d3748;
}

.check-icon {
  flex-shrink: 0;
}

.careers-section {
  margin-bottom: 2rem;
}

.careers {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.career-tag {
  padding: 0.5rem 1rem;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #4a5568;
}

.compatibility-section {
  margin-top: 2rem;
}

.compatibility-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.compatibility-item {
  padding: 1rem;
  border-radius: 12px;
  background: #f7fafc;
}

.compatibility-item.best {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
}

.compatibility-item.good {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
}

.compatibility-item.challenging {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
}

.compatibility-label {
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
  color: #1a202c;
}

.animals {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.animal-item-with-reason {
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 10px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.animal-item-with-reason:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background: rgba(255, 255, 255, 0.9);
}

.animal-header {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.animal-emoji-small {
  font-size: 1.5rem;
  line-height: 1;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.animal-name-small {
  font-size: 0.95rem;
  color: #2d3748;
  font-weight: 600;
}

.animal-reason {
  font-size: 0.85rem;
  color: #4a5568;
  line-height: 1.5;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

@media (max-width: 768px) {
  .result-title-section {
    padding: 1.5rem 1rem 0.75rem 1rem;
  }

  .result-title {
    font-size: 1.75rem;
  }

  .result-subtitle {
    font-size: 1rem;
  }

  .card-header {
    flex-direction: column;
    text-align: center;
  }

  .animal-emoji {
    font-size: 4rem;
  }

  .animal-name {
    font-size: 1.5rem;
  }

  .compatibility-grid {
    grid-template-columns: 1fr;
  }
}
</style>

